import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import debounce from "lodash.debounce";
import DashboardNavbar from "../DashboardNavbar";
import { useLoading } from "../LoadingContext";
import Pagination from "../../Pagination";

function EmployeeDashboard() {
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const { loading, setLoading } = useLoading();
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [anniversaryDate, setAnniversaryDate] = useState("");
  const [sortField, setSortField] = useState(""); // Sorting field
  const [sortOrder, setSortOrder] = useState("asc"); // Sorting order: "asc" or "desc"

  // Debounce the search input
  const debouncedSearch = debounce((query) => {
    setSearch(query);
  }, 100); // 100ms delay

  const handleSearch = (e) => {
    debouncedSearch(e.target.value); // Use debounced function to avoid lag
  };

  // Fetch data from the API
  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/bgv_form/personal-info-det/`
      );
      const fetchedData = response.data || [];
      setData(fetchedData);
      setFilteredData(fetchedData);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  const fetchVerificationStatuses = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/bgv_form/personal-info-det/`
      );
      const uniqueStatuses = new Set(
        response.data.map((item) => (item.fully_verified ? "Yes" : "No"))
      );
      setStatuses(Array.from(uniqueStatuses));
    } catch (error) {
      console.error("Error fetching statuses:", error);
    }
  };

  const applyFilters = useCallback(() => {
    let filtered = [...data];

    // Handle search filter
    if (search) {
      filtered = filtered.filter(
        (item) =>
          (item.personal_info.first_name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (item.personal_info.last_name || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (item.personal_info.email_id || "")
            .toLowerCase()
            .includes(search.toLowerCase()) ||
          (item.personal_info.phone_number || "").toString().includes(search)
      );
    }

    // Handle verification status filter
    if (selectedStatus) {
      filtered = filtered.filter(
        (item) =>
          (item.fully_verified ? "Yes" : "No").toLowerCase() ===
          selectedStatus.toLowerCase()
      );
    }

    // Handle date filters
    if (dateOfBirth) {
      filtered = filtered.filter(
        (item) =>
          item.personal_info.date_of_birth &&
          new Date(item.personal_info.date_of_birth)
            .toISOString()
            .slice(0, 10) === dateOfBirth
      );
    }

    if (fromDate) {
      filtered = filtered.filter(
        (item) =>
          item.personal_info.date_of_joining &&
          new Date(item.personal_info.date_of_joining) >= new Date(fromDate)
      );
    }

    if (toDate) {
      filtered = filtered.filter(
        (item) =>
          item.personal_info.date_of_joining &&
          new Date(item.personal_info.date_of_joining) <= new Date(toDate)
      );
    }

    if (anniversaryDate) {
      filtered = filtered.filter(
        (item) =>
          item.personal_info.anniversary_day &&
          new Date(item.personal_info.anniversary_day)
            .toISOString()
            .slice(0, 10) === anniversaryDate
      );
    }

    // Apply sorting to the filtered data
    const sorted = filtered.sort((a, b) => {
      if (!sortField) return 0;
      const fieldA = a.personal_info[sortField] || "";
      const fieldB = b.personal_info[sortField] || "";
      if (sortOrder === "asc") return fieldA > fieldB ? 1 : -1;
      return fieldA < fieldB ? 1 : -1;
    });

    setFilteredData(sorted);
    setCurrentPage(1); // Reset to the first page
  }, [
    data,
    search,
    selectedStatus,
    dateOfBirth,
    fromDate,
    toDate,
    anniversaryDate,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    fetchData();
    fetchVerificationStatuses();
  }, []);

  useEffect(() => {
    applyFilters(); // Apply filters after data or filter state changes
  }, [applyFilters]); // Dependencies

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
    setCurrentPage(1);
  };

  // Function to clear dropdowns
  const handleClearOptions = () => {
    setSearch("");
    setSelectedStatus("");
    setDateOfBirth("");
    setFromDate("");
    setToDate("");
    setAnniversaryDate("");
    setSortField("");
    setSortOrder("asc");
    setFilteredData(data);
    setCurrentPage(1);
  };

  const handleSort = (field) => {
    const order = sortField === field && sortOrder === "asc" ? "desc" : "asc";
    setSortField(field);
    setSortOrder(order);
  };

  const getSortArrow = (field) => {
    if (sortField !== field) return "▲▼"; // Display both arrows by default
    return sortOrder === "asc" ? "▲" : "▼"; // Show the active arrow based on sorting order
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  if (loading) {
    return (
      <div
        className="container d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <div>
          <div
            className="spinner-border text-dark"
            role="status"
            style={{ width: "3rem", height: "3rem" }}
          >
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
        <h3 className="text-center text-dark">&nbsp;Data is Fetching.... </h3>
      </div>
    );
  }

  return (
    <>
      <DashboardNavbar />
      <div className="container py-3 main-content">
        <div className="p-2 mb-1 rounded">
          <h3 className="text-center font_header">Employee Dashboard</h3>

          <div className="row align-items-center mt-1">
            <div className="col-12 col-lg-4 mb-2 mb-lg-0">
              <div className="search">
                <form className="form-inline text-dark fw-bold">
                  <div className="col-lg-7 col-md-5">
                    <label htmlFor="search" className="form-label">
                      Search:
                    </label>
                    <input
                      className="form-control mt-1 border-dark text-dark"
                      type="search"
                      title="Search first name,last name,email id,phone number"
                      aria-label="Search"
                      onChange={handleSearch}
                      value={search}
                    />
                  </div>
                </form>
              </div>
            </div>
            <div className="d-flex align-items-start justify-content-between">
              <form className="row g-3 align-items-center flex-grow-1 fw-bold">
                <div className="col-lg-2 col-md-5 mb-1">
                  <label
                    htmlFor="verificationStatus"
                    className="form-label text-"
                  >
                    Search by Verification Status:
                  </label>
                  <div className="d-flex align-items-center">
                    <select
                      className="form-select select-box border-dark"
                      id="verificationStatus"
                      title="Search By Verification Status"
                      aria-label="Verification Status"
                      value={selectedStatus}
                      onChange={handleStatusChange}
                    >
                      <option value="" disabled>
                        Select Yes or No
                      </option>
                      {statuses.map((status) => (
                        <option key={status} value={status}>
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="dateOfBirth" className="form-label">
                    Search By Date of Birth:
                  </label>
                  <input
                    className="form-control border-dark bg-transparent"
                    type="date"
                    id="dateOfBirth"
                    title="Date Of Birth"
                    aria-label="Date Of Birth"
                    value={dateOfBirth}
                    onChange={(e) => setDateOfBirth(e.target.value)}
                  />
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="fromDate" className="form-label">
                    Search by Joining Date(From Date):
                  </label>
                  <input
                    className="form-control border-dark bg-transparent"
                    type="date"
                    id="fromDate"
                    title="From Date"
                    aria-label="From Date"
                    value={fromDate}
                    onChange={(e) => setFromDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label htmlFor="toDate" className="form-label">
                    Search by Joining Date(To Date):
                  </label>
                  <input
                    className="form-control border-dark bg-transparent"
                    type="date"
                    id="toDate"
                    title="To Date"
                    aria-label="To Date"
                    value={toDate}
                    onChange={(e) => setToDate(e.target.value)}
                  />
                </div>
                <div className="col-lg-2 col-md-5 mb-1">
                  <label
                    htmlFor="anniversaryDate"
                    className="form-label border-dark text-"
                  >
                    Search By Anniversary Date:
                  </label>
                  <div className="d-flex align-items-center">
                    <input
                      className="form-control border-dark bg-transparent"
                      type="date"
                      id="anniversaryDate"
                      title="Anniversary Date"
                      aria-label="Anniversary Date"
                      value={anniversaryDate}
                      onChange={(e) => setAnniversaryDate(e.target.value)}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-danger ms-1 btn-lg"
                      title="Clear All"
                      onClick={handleClearOptions}
                    >
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
        <div className="row">
          <div
            className="table-responsive table-wrapper text-center"
            style={{ flex: 1 }}
          >
            <table
              className="table table-hover table-bordered border-dark"
              style={{
                tableLayout: "fixed",
                width: "100%",
                minWidth: "800px",
              }}
            >
              <thead className="tb_header border-dark">
                <tr>
                  <th
                    className={sortField === "id" ? "sorted" : ""}
                    onClick={() => handleSort("id")}
                    style={{
                      width: "100px",
                      cursor: "pointer",
                    }}
                  >
                    ID
                    <span className="sort-arrow">{getSortArrow("id")}</span>
                  </th>
                  <th
                    onClick={() => handleSort("fully_verified")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Verification&nbsp;Status
                    <span className="sort-arrow">
                      {getSortArrow("fully_verified")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("first_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    First&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("first_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("middle_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Middle&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("middle_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("last_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Last&nbsp;Name
                    {getSortArrow("last_name")}
                  </th>
                  <th
                    onClick={() => handleSort("email_id")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Email
                    {getSortArrow("email_id")}
                  </th>
                  <th
                    onClick={() => handleSort("phone_number")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Phone&nbsp;Number
                    {getSortArrow("phone_number")}
                  </th>
                  <th
                    onClick={() => handleSort("marital_status")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Marital Status
                    {getSortArrow("marital_status")}
                  </th>
                  <th
                    onClick={() => handleSort("gender")}
                    style={{ width: "150px", cursor: "pointer" }}
                  >
                    Gender
                    {getSortArrow("gender")}
                  </th>
                  <th
                    onClick={() => handleSort("blood_group")}
                    style={{ width: "150px", cursor: "pointer" }}
                  >
                    Blood&nbsp;Group
                    {getSortArrow("blood_group")}
                  </th>
                  <th
                    onClick={() => handleSort("nationality")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Nationality
                    <span className="sort-arrow">
                      {getSortArrow("nationality")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("permanent_address")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Permanent&nbsp;Address
                    <span className="sort-arrow">
                      {getSortArrow("permanent_address")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("current_address")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Current&nbsp;Address
                    <span className="sort-arrow">
                      {getSortArrow("current_address")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("aadhar_number")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Aadhar Number
                    <span className="sort-arrow">
                      {getSortArrow("aadhar_number")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("pan_number")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    PAN Number
                    <span className="sort-arrow">
                      {getSortArrow("pan_number")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("date_of_birth")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Date&nbsp;Of&nbsp;Birth
                    <span className="sort-arrow">
                      {getSortArrow("date_of_birth")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("date_of_joining")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Date&nbsp;Of&nbsp;Joining
                    <span className="sort-arrow">
                      {getSortArrow("date_of_joining")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("anniversary_day")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Anniversary&nbsp;Day
                    <span className="sort-arrow">
                      {getSortArrow("anniversary_day")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("father_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Father&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("father_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("father_phone_number")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Father Phone No.
                    <span className="sort-arrow">
                      {getSortArrow("father_phone_number")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("mother_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Mother&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("mother_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("mother_phone_number")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Mother Phone No.
                    <span className="sort-arrow">
                      {getSortArrow("mother_phone_number")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("spouse_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Spouse&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("spouse_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("spouse_phone_number")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Spouse Phone No
                    <span className="sort-arrow">
                      {getSortArrow("spouse_phone_number")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("spouse_blood_group")}
                    style={{ width: "250px", cursor: "pointer" }}
                  >
                    Spouse Blood&nbsp;Group
                    <span className="sort-arrow">
                      {getSortArrow("spouse_blood_group")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("child1_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Child1&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("child1_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("child2_name")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Child2&nbsp;Name
                    <span className="sort-arrow">
                      {getSortArrow("child2_name")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("emergencyContact")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Emergency&nbsp;Contact
                    <span className="sort-arrow">
                      {getSortArrow("emergencyContact")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("employmentHistory")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Employment&nbsp;History
                    <span className="sort-arrow">
                      {getSortArrow("employmentHistory")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("educationHistory")}
                    style={{ width: "300px", cursor: "pointer" }}
                  >
                    Education&nbsp;History
                    <span className="sort-arrow">
                      {getSortArrow("educationHistory")}
                    </span>
                  </th>

                  <th
                    onClick={() => handleSort("skills")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Skills
                    <span className="sort-arrow">{getSortArrow("skills")}</span>
                  </th>
                  {/* <th
                    onClick={() => handleSort("documents")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Documents
                    <span className="sort-arrow">
                      {getSortArrow("documents")}
                    </span>
                  </th> */}
                  <th
                    onClick={() => handleSort("criminal_record")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Criminal Record
                    <span className="sort-arrow">
                      {getSortArrow("criminal_recors")}
                    </span>
                  </th>
                  <th
                    onClick={() => handleSort("health_record")}
                    style={{ width: "200px", cursor: "pointer" }}
                  >
                    Health Record
                    <span className="sort-arrow">
                      {getSortArrow("health_record")}
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody className="text-center table-light border-dark">
                {currentItems.length > 0 ? (
                  currentItems.map((item) => {
                    const {
                      personal_info: personalInfo = {},
                      emergency_contact: emergencyContact = [],
                      employment_history: employmentHistory = [],
                      education_history: educationHistory = [],
                      // uploaded_documents: uploadedDocuments = [],
                      criminal_record: criminalRecord = [],
                      health_record: healthRecord = [],
                      skills = [],
                    } = item;

                    return (
                      <tr key={personalInfo.id || Math.random()}>
                        {/* Personal Info */}
                        <td>{personalInfo.id || "N/A"}</td>
                        <td>{item.fully_verified ? "Yes" : "No"}</td>
                        {/* <td>{overall_hr_verified ? "Yes" : "No"}</td> */}
                        <td>
                          <Link
                            to={`/verification_form/${personalInfo.id || ""}`}
                          >
                            {personalInfo.first_name || "N/A"}
                          </Link>
                        </td>
                        <td>{personalInfo.middle_name || "N/A"}</td>
                        <td>{personalInfo.last_name || "N/A"}</td>
                        <td>{personalInfo.email_id || "N/A"}</td>
                        <td>{personalInfo.phone_number || "N/A"}</td>
                        <td>{personalInfo.marital_status || "N/A"}</td>
                        <td>{personalInfo.gender || "N/A"}</td>
                        <td>{personalInfo.blood_group || "N/A"}</td>
                        <td>{personalInfo.nationality || "N/A"}</td>
                        <td>{personalInfo.permanent_address || "N/A"}</td>
                        <td>{personalInfo.current_address || "N/A"}</td>
                        <td>{personalInfo.aadhar_number || "N/A"}</td>
                        <td>{personalInfo.pan_number || "N/A"}</td>
                        <td>{personalInfo.date_of_birth || "N/A"}</td>
                        <td>{personalInfo.date_of_joining || "N/A"}</td>
                        <td>{personalInfo.anniversary_day || "N/A"}</td>
                        <td>{personalInfo.father_name || "N/A"}</td>
                        <td>{personalInfo.father_phone_number || "N/A"}</td>
                        <td>{personalInfo.mother_name || "N/A"}</td>
                        <td>{personalInfo.mother_phone_number || "N/A"}</td>
                        <td>{personalInfo.spouse_name || "N/A"}</td>
                        <td>{personalInfo.spouse_phone_number || "N/A"}</td>
                        <td>{personalInfo.spouse_blood_group || "N/A"}</td>
                        <td>{personalInfo.child1_name || "N/A"}</td>
                        <td>{personalInfo.child2_name || "N/A"}</td>

                        {/* Emergency Contact */}
                        <td>
                          {emergencyContact.length > 0
                            ? emergencyContact
                                .map(
                                  (contact) =>
                                    `${contact.name || "N/A"} (${
                                      contact.phone_number || "N/A"
                                    })`
                                )
                                .join(", ")
                            : "N/A"}
                        </td>

                        {/* Employment History */}
                        <td>
                          {employmentHistory.length > 0
                            ? employmentHistory
                                .map(
                                  (employment) =>
                                    `${employment.company_name || "N/A"} (${
                                      employment.employee_designation || "N/A"
                                    })`
                                )
                                .join(", ")
                            : "N/A"}
                        </td>

                        {/* Education History */}
                        <td>
                          {educationHistory.length > 0
                            ? educationHistory
                                .map(
                                  (education) =>
                                    `${education.qualification || "N/A"} (${
                                      education.college_name || "N/A"
                                    })`
                                )
                                .join(", ")
                            : "N/A"}
                        </td>

                        {/* Skills */}
                        <td>
                          {skills.length > 0
                            ? skills
                                .map((skill) => skill.skill_name || "N/A")
                                .join(", ")
                            : "N/A"}
                        </td>

                        {/* Criminal Record */}
                        <td>
                          {criminalRecord.length > 0
                            ? criminalRecord.some(
                                (record) => record.criminal_record
                              )
                              ? "Yes"
                              : "No"
                            : "N/A"}
                        </td>

                        {/* Health Record */}
                        <td>
                          {healthRecord.length > 0
                            ? healthRecord.some(
                                (health) => health.medical_conditions
                              )
                              ? "Yes"
                              : "No"
                            : "N/A"}
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="100" className="text-start">
                      <h4>No data available</h4>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {/* Pagination Code */}
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        paginate={(page) => setCurrentPage(page)}
      />
    </>
  );
}

export default EmployeeDashboard;
